import type { DestinationIconName } from "./nav-icon";

/**
 * Work domains and their hue (tokens `--c-domain-*`). The page header icon
 * chip, the active tab and KPI tiles take the hue of the page's domain so an
 * operator can tell calculations, rules, research and the IOB agent apart at
 * a glance. A domain hue is identity only; status keeps its own ramps.
 */
export type PageAccent = "home" | "calc" | "rules" | "research" | "agent" | "admin";

type AccentMatch = { prefix: string; accent: PageAccent; icon: DestinationIconName };

/** Longest prefix first: `/rules/verification` must win over `/rules`. */
const ACCENT_ROUTES: readonly AccentMatch[] = [
  { prefix: "/overview", accent: "home", icon: "overview" },
  { prefix: "/calculations/simulate", accent: "calc", icon: "calculator" },
  { prefix: "/calculations", accent: "calc", icon: "calculations" },
  { prefix: "/lots", accent: "calc", icon: "lots" },
  { prefix: "/invoices", accent: "rules", icon: "invoices" },
  { prefix: "/rules/verification", accent: "rules", icon: "verification" },
  { prefix: "/rules", accent: "rules", icon: "rules" },
  { prefix: "/catalog", accent: "rules", icon: "rules" },
  { prefix: "/rulesets", accent: "rules", icon: "rulesets" },
  { prefix: "/proposals", accent: "rules", icon: "proposals" },
  { prefix: "/evidence", accent: "research", icon: "evidence" },
  { prefix: "/sources", accent: "research", icon: "sources" },
  { prefix: "/admin/web-research", accent: "research", icon: "webResearch" },
  { prefix: "/admin/agent-findings", accent: "agent", icon: "agentFindings" },
  { prefix: "/admin/agents", accent: "agent", icon: "agents" },
  { prefix: "/admin/demos", accent: "agent", icon: "demos" },
  { prefix: "/admin/how-it-works", accent: "admin", icon: "howItWorks" },
  { prefix: "/admin/upload-failures", accent: "admin", icon: "uploadFailures" },
  { prefix: "/admin/sessions", accent: "admin", icon: "sessions" },
  { prefix: "/admin/infra-cost", accent: "admin", icon: "infraCost" },
  { prefix: "/admin/auto-approval", accent: "admin", icon: "autoApproval" },
  { prefix: "/audit", accent: "admin", icon: "audit" },
  { prefix: "/users", accent: "admin", icon: "users" },
  { prefix: "/legal", accent: "admin", icon: "legal" },
  { prefix: "/settings", accent: "admin", icon: "settings" },
];

export function pageAccentFor(pathname: string): {
  accent: PageAccent;
  icon: DestinationIconName | null;
} {
  const match = ACCENT_ROUTES.find(
    (entry) => pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`),
  );
  return match ? { accent: match.accent, icon: match.icon } : { accent: "admin", icon: null };
}
